import {
  getFieldTypeOcticon,
  type IssueFieldSingleSelectOption,
  toIssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {
  getIssueFieldSelectionPresentationValue,
  type IssueFieldWithUnsavedValue,
} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import {TooltipTriggerWrapper} from '@github-ui/issue-metadata/TooltipTriggerWrapper'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'
import {colorNames, useNamedColor} from '@github-ui/use-named-color'
import {GlobeIcon, type IconProps, PlusIcon} from '@primer/octicons-react'
import {type ButtonProps, IconButton, Tooltip, useId} from '@primer/react'

import {LABELS} from '../constants/labels'
import styles from './MetadataFooter.module.css'

const GlobeIconWithProps = (props: IconProps) => <GlobeIcon {...props} size={12} />

export type IssueFieldValueSharedEditorAnchorProps = {
  fieldWithValue: IssueFieldWithUnsavedValue
  buttonProps?: ButtonProps
  className?: string
  readonly?: boolean
  showVisibilityIndicator?: boolean
}

export const IssueFieldValueSharedEditorAnchor = ({
  fieldWithValue,
  buttonProps,
  className,
  readonly,
  showVisibilityIndicator,
}: IssueFieldValueSharedEditorAnchorProps) => {
  const fieldType = toIssueFieldType(fieldWithValue.field.dataType)
  const text = getIssueFieldSelectionPresentationValue(fieldType, fieldWithValue.value)
  const leadingIconElement = getLeadingIconElement(fieldWithValue)
  const anchorText = text ? `${fieldWithValue.field.name}:` : fieldWithValue.field.name!
  const visibilityTooltipId = useId()

  const picker = (
    <SharedPicker
      key={fieldWithValue.field.id}
      anchorText={anchorText}
      sharedPickerMainValue={text}
      leadingIcon={getFieldTypeOcticon(fieldType)}
      leadingIconElement={leadingIconElement}
      trailingIcon={showVisibilityIndicator ? GlobeIconWithProps : undefined}
      anchorProps={buttonProps}
      className={className}
      readonly={readonly}
      visibilityTooltipId={showVisibilityIndicator ? visibilityTooltipId : undefined}
    />
  )
  return showVisibilityIndicator ? (
    <Tooltip
      id={visibilityTooltipId}
      text={LABELS.visibilityIndicatorTooltip}
      type="description"
      direction="s"
      delay="medium"
    >
      <TooltipTriggerWrapper>{picker}</TooltipTriggerWrapper>
    </Tooltip>
  ) : (
    picker
  )
}

const getLeadingIconElement = (fieldWithValue: IssueFieldWithUnsavedValue) => {
  if (fieldWithValue.field.dataType === 'SINGLE_SELECT' && fieldWithValue.value) {
    const option = fieldWithValue.value as IssueFieldSingleSelectOption
    const color = option?.color || 'GRAY'

    return <SingleSelectFieldDot color={color} />
  }

  return null
}

const SingleSelectFieldDot = ({color}: {color: string}) => {
  const effectiveColor = colorNames.find(c => c === color)
  const {fg} = useNamedColor(effectiveColor)
  return <div style={{'--bg': `${fg}`} as React.CSSProperties} className={styles.SingleSelectFieldDot} />
}

export const addIssueFieldAnchor = (
  anchorProps: React.HTMLAttributes<HTMLElement>,
  ref?: React.Ref<HTMLButtonElement>,
) => {
  return (
    <IconButton
      onClick={event => {
        anchorProps.onClick?.(event)
      }}
      ref={ref}
      icon={PlusIcon}
      variant="default"
      aria-label={LABELS.setAdditionalFields}
      tooltipDirection="n"
      size="small"
    />
  )
}
